import styled from "styled-components";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import React from "react";

import { theme } from "./theme";
import { useT } from "./i18n";
import { formatDateTime } from "./dateFormat";

/* The housekeeping checkout report, in the drawer, where the refund decision is made.
 * Collapsed by default: on a clean room there is nothing to read, and the FO's next
 * action is the refund below. A room with issues opens itself, because that is the
 * case where the report IS the decision.
 */
const Box = styled.div`
  background: ${(props) => (props.$alert ? theme.dangerBg : "#fff8ec")};
  border: 1px solid
    ${(props) => (props.$alert ? theme.dangerBorder : "#f0dfc0")};
  border-radius: 10px;
  margin: 0.45rem 0 0.2rem;
  padding: 0.55rem 0.7rem;
`;

const Head = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  color: ${theme.ink};
  cursor: pointer;
  display: flex;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 0.35rem;
  padding: 0;
  text-align: left;
  width: 100%;
`;

const HeadText = styled.span`
  flex: 1 1 auto;
`;

const Count = styled.span`
  background: ${(props) => (props.$alert ? theme.danger : "#1b6e56")};
  border-radius: 999px;
  color: #ffffff;
  flex: 0 0 auto;
  font-size: 0.68rem;
  font-weight: 800;
  padding: 0.1rem 0.45rem;
`;

const Body = styled.div`
  margin-top: 0.5rem;
`;

const Facts = styled.dl`
  display: grid;
  gap: 0.15rem 0.6rem;
  grid-template-columns: auto 1fr;
  margin: 0 0 0.5rem;
`;

const Term = styled.dt`
  color: #5e6278;
  font-size: 0.74rem;
  font-weight: 700;
`;

const Detail = styled.dd`
  color: ${theme.ink};
  font-size: 0.74rem;
  margin: 0;
  word-break: break-word;
`;

const Note = styled.div`
  background: rgba(255, 255, 255, 0.7);
  border-radius: 7px;
  color: ${theme.ink};
  font-size: 0.78rem;
  line-height: 1.45;
  margin-bottom: 0.5rem;
  padding: 0.4rem 0.5rem;
  white-space: pre-wrap;
`;

const Item = styled.div`
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  padding: 0.3rem 0;
`;

const ItemMain = styled.div`
  min-width: 0;
`;

const ItemName = styled.div`
  color: ${(props) => (props.$issue ? theme.danger : theme.ink)};
  font-size: 0.78rem;
  font-weight: ${(props) => (props.$issue ? 800 : 600)};
`;

const ItemMeta = styled.div`
  color: #5e6278;
  font-size: 0.7rem;
  margin-top: 0.05rem;
`;

const ItemValue = styled.div`
  color: ${(props) => (props.$issue ? theme.danger : theme.ink700)};
  flex: 0 0 auto;
  font-size: 0.78rem;
  font-weight: 700;
  text-align: right;
`;

const Photos = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.5rem;
`;

const Photo = styled.a`
  display: block;
  height: 64px;
  width: 64px;

  img {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 6px;
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`;

const Source = styled.span`
  background: ${(props) => (props.$fo ? "#e8f0fe" : "#efece4")};
  border-radius: 999px;
  color: ${(props) => (props.$fo ? "#1a4d8f" : "#5e6278")};
  font-size: 0.66rem;
  font-weight: 800;
  padding: 0.05rem 0.4rem;
`;

// props: { report }
const HkReportBlock = (props) => {
  const { t, lang } = useT();
  const report = props.report;
  const alert = report ? !report.room_ok : false;
  const [open, setOpen] = React.useState(alert);

  if (!report) return null;

  const fact = (label, value) =>
    value ? (
      <>
        <Term>{label}</Term>
        <Detail>{value}</Detail>
      </>
    ) : null;

  return (
    <Box $alert={alert}>
      <Head type="button" onClick={() => setOpen((wasOpen) => !wasOpen)}>
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        {alert ? (
          <AlertTriangle size={13} color={theme.danger} />
        ) : (
          <CheckCircle2 size={13} color="#1b6e56" />
        )}
        <HeadText>
          {alert ? t("hk_report_issues_title") : t("hk_report_ok_title")}
        </HeadText>
        <Count $alert={alert}>
          {alert
            ? t("hk_report_flagged").replace("%{count}", report.issue_count)
            : t("hk_report_all_good")}
        </Count>
      </Head>

      {open ? (
        <Body>
          <Facts>
            {fact(
              t("hk_report_submitted_at"),
              report.submitted_at
                ? formatDateTime(report.submitted_at, lang)
                : null,
            )}
            {report.filed_by ? (
              <>
                <Term>{t("hk_report_filed_by")}</Term>
                <Detail>
                  {report.filed_by}{" "}
                  <Source $fo={report.filed_by_front_office}>
                    {report.filed_by_front_office
                      ? t("hk_report_source_fo")
                      : t("hk_report_source_hk")}
                  </Source>
                </Detail>
              </>
            ) : null}
            {fact(t("hk_report_supervisor"), report.floor_supervisor_name)}
            {fact(
              t("hk_report_cleaned_at"),
              report.cleaned_at
                ? formatDateTime(report.cleaned_at, lang)
                : null,
            )}
          </Facts>

          {report.notes ? <Note>{report.notes}</Note> : null}
          {report.cleaning_notes ? <Note>{report.cleaning_notes}</Note> : null}

          {report.rows.map((row) => (
            <Item key={row.field_id}>
              <ItemMain>
                <ItemName $issue={row.issue}>{row.item_name || "—"}</ItemName>
                <ItemMeta>
                  {[row.category, row.type_label].filter(Boolean).join(" · ")}
                  {row.note ? ` · ${row.note}` : ""}
                </ItemMeta>
              </ItemMain>
              <ItemValue $issue={row.issue}>{row.reported}</ItemValue>
            </Item>
          ))}

          {report.images.length > 0 ? (
            <Photos>
              {report.images.map((image) => (
                <Photo
                  key={image.id}
                  href={image.url}
                  target="_blank"
                  rel="noreferrer"
                  title={image.caption || t("hk_report_photo")}
                >
                  <img
                    src={image.url}
                    alt={image.caption || t("hk_report_photo")}
                  />
                </Photo>
              ))}
            </Photos>
          ) : null}
        </Body>
      ) : null}
    </Box>
  );
};

export default HkReportBlock;
